import { snakeToCamel, type CamelCaseKeys } from '../api/converters/snakeToCamel';
import { Logger } from '../logger/Logger';
import { SessionStorage } from '../storage/session/SessionStorage';

export const defaultRawSession: IUserSessionRaw = {
  JSESSIONID: '',
  actual_web_server: '',
  casino_id: '',
  config_url: '',
  game_mode: '',
  gametype: '',
  hasQuickGuide: '',
  lang: '',
  meta_server: '',
  socket_port: '',
  socket_server: '',
  stats_collector_uuid: '',
  table_id: '',
  token: '',
  uiversion: '',
  web_server: '',
};

export interface IUserSessionRaw extends Record<string, string> {
  JSESSIONID: string;
  actual_web_server: string;
  casino_id: string;
  config_url: string;
  game_mode: string;
  gametype: string;
  hasQuickGuide: string;
  lang: string;
  meta_server: string;
  socket_port: string;
  socket_server: string;
  stats_collector_uuid: string;
  table_id: string;
  token: string;
  uiversion: string;
  web_server: string;
}

export interface IUserSession extends CamelCaseKeys<IUserSessionRaw> {}

const isProduction = process.env.NODE_ENV === 'production';

export class UserSession {
  static session?: IUserSession | null;
  static URLSession = UserSession.parseUrlSession();
  static sessionKey = window.name === 'lobby_iframe' ? 'PPG_L' : 'PPG'; // TODO: VERY SAD, IS BAD, NEEDS TO BE FIXED

  private static parseUrlSession(): IUserSessionRaw | null {
    if (!window.location.search) return null;
    const sessionResult: IUserSessionRaw = { ...defaultRawSession };
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      sessionResult[key] = value === 'null' ? '' : value;
    });
    return sessionResult;
  }

  private static postProcessSession(session: IUserSessionRaw): IUserSession {
    const processedSession = { ...session };

    if (processedSession && !processedSession.meta_server) {
      processedSession.meta_server = processedSession.actual_web_server;
    }

    if (!isProduction && processedSession) {
      const urlsToPrefix = ['actual_web_server', 'web_server', 'meta_server'];
      urlsToPrefix.forEach((url) => {
        if (processedSession[url] && !processedSession[url].includes('proxy')) {
          processedSession[url] = '/proxy/' + processedSession[url];
        }
      });
    }

    return snakeToCamel(processedSession);
  }

  private static getStoredSession(): IUserSessionRaw | null {
    const storedSession = SessionStorage.getJson<IUserSessionRaw>(UserSession.sessionKey);
    if (storedSession) {
      return storedSession;
    }
    return null;
  }

  public static init(): void {
    const rawSession = UserSession.URLSession || UserSession.getStoredSession();
    if (rawSession) {
      UserSession.session = UserSession.postProcessSession(rawSession);
      SessionStorage.setJson(UserSession.sessionKey, rawSession);
    } else {
      Logger.logFailure('errors', 'session', 'empty', Date.now(), 1, '', false);
    }
  }

  public static getSession(): IUserSession | null | undefined {
    /** We check if session already had attempted initialisation, and only if it has not, we initialise it */
    if (UserSession.session === undefined) {
      UserSession.init();
    }
    return UserSession.session;
  }

  public static getURLSession(): IUserSession | null {
    return UserSession.URLSession ? UserSession.postProcessSession(UserSession.URLSession) : null;
  }
}
